/**
 * https://github.com/ultimate-pagination/react-ultimate-pagination/blob/master/src/react-ultimate-pagination.js
 * Вот так. Потому что, проблемы с зависимостями у пакета
 */

import {IPaginationProps} from 'components/Pagination';
import React, {FC, useMemo} from 'react';
import {getPaginationModel, ITEM_TYPES} from 'ultimate-pagination';
import {MAX_START_SIBLING_RANGE_PAGE, SIBLING_PAGES_RANGE} from './constants';

// eslint-disable-next-line id-length
const renderItemComponentFunctionFactory = (
    itemTypeToComponent: Record<keyof typeof ITEM_TYPES, FC<IPaginationProps>>,
    currentPage: number,
    onChange: (value: number) => void,
) => {
    const onItemClickFunctionFactory = ({value, isDisabled}: {value: number; isDisabled: boolean}) => {
        return () => {
            if (!isDisabled && onChange && currentPage !== value) {
                onChange(value);
            }
        };
    };

    return function renderItemComponent(props: {
        isActive: boolean;
        value: number;
        isDisabled: boolean;
        type: keyof typeof ITEM_TYPES;
        key?: React.Key;
    }) {
        const {key, type, ...restProps} = props as typeof props & {key?: React.Key};
        const ItemComponent = itemTypeToComponent[type];
        const onItemClick = onItemClickFunctionFactory(props);
        return <ItemComponent key={key} onClick={onItemClick} {...restProps} />;
    };
};

interface IProps {
    currentPage: number;
    totalPages: number;
    boundaryPagesRange?: number;
    siblingPagesRange?: number;
    hideEllipsis?: boolean;
    hidePreviousAndNextPageLinks?: boolean;
    hideFirstAndLastPageLinks?: boolean;
    onChange: (value: number) => void;
    disabled?: boolean;
    className?: string;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const createUltimatePagination = ({
    itemTypeToComponent,
}: {
    itemTypeToComponent: Record<keyof typeof ITEM_TYPES, FC<IPaginationProps>>;
}) => {
    return (props: IProps) => {
        const {
            currentPage,
            totalPages,
            boundaryPagesRange,
            siblingPagesRange = SIBLING_PAGES_RANGE,
            hideEllipsis,
            hidePreviousAndNextPageLinks,
            hideFirstAndLastPageLinks,
            onChange,
            disabled,
            ...restProps
        } = props;

        const siblingPagesRangeValue = useMemo(
            () => (MAX_START_SIBLING_RANGE_PAGE > currentPage || currentPage === totalPages ? 1 : siblingPagesRange),
            [currentPage, siblingPagesRange, totalPages],
        );

        const paginationModel = getPaginationModel({
            boundaryPagesRange,
            currentPage,
            hideEllipsis,
            hideFirstAndLastPageLinks,
            hidePreviousAndNextPageLinks,
            siblingPagesRange: siblingPagesRangeValue,
            totalPages,
        });
        const renderItemComponent = renderItemComponentFunctionFactory(itemTypeToComponent, currentPage, onChange);
        return (
            <div {...restProps}>
                {paginationModel.map((itemModel) => {
                    let isDisabled = false;

                    if ('NEXT_PAGE_LINK' === itemModel.type && currentPage >= totalPages) {
                        isDisabled = true;
                    }

                    if ('PREVIOUS_PAGE_LINK' === itemModel.type && 1 === currentPage) {
                        isDisabled = true;
                    }

                    return renderItemComponent({
                        ...itemModel,
                        isDisabled,
                    });
                })}
            </div>
        );
    };
};

export {ITEM_TYPES};
